import { NavigationItem } from '../types/navigation';

/**
 * Static navigation configuration for the sidebar.
 *
 * `label` values are i18n keys resolved via the 'layout' namespace.
 */
export const NAV_ITEMS: NavigationItem[] = [
  {
    id: 'overview',
    label: 'nav.overview',
    path: '/',
    icon: 'LayoutDashboard',
  },
  {
    id: 'leaderboard',
    label: 'nav.leaderboard',
    path: '/leaderboard',
    icon: 'Trophy',
  },
  {
    id: 'engineers',
    label: 'nav.engineers',
    path: '/engineers',
    icon: 'Users',
  },
  {
    id: 'teams',
    label: 'nav.teams',
    path: '/teams',
    icon: 'Network',
  },
  {
    id: 'pull-requests',
    label: 'nav.pullRequests',
    path: '/pull-requests',
    icon: 'GitPullRequest',
  },
  {
    id: 'work-items',
    label: 'nav.workItems',
    path: '/work-items',
    icon: 'CheckSquare',
  },
  {
    id: 'repositories',
    label: 'nav.repositories',
    path: '/repositories',
    icon: 'FolderGit2',
  },
  {
    id: 'settings',
    label: 'nav.settings',
    path: '/settings',
    icon: 'Settings',
  },
];

/** Govern hub destinations — nested under the sidebar Govern section when Next shell is on. */
export const GOVERN_HUB_NAV_ITEMS: NavigationItem[] = [
  {
    id: 'govern-adrs',
    label: 'nav.next.governAdrs',
    path: '/govern/adrs',
    icon: 'Scale',
  },
  {
    id: 'govern-coding-standards',
    label: 'nav.next.governCodingStandards',
    path: '/govern/coding-standards',
    icon: 'FileCode2',
  },
  {
    id: 'govern-repository-profiles',
    label: 'nav.next.governRepositoryProfiles',
    path: '/govern/repository-profiles',
    icon: 'Layers',
  },
  {
    id: 'govern-rules',
    label: 'nav.next.governRules',
    path: '/govern/rules',
    icon: 'BookMarked',
  },
  // Hidden from the Govern menu per request — re-enable by uncommenting.
  // {
  //   id: 'govern-ai-controls',
  //   label: 'nav.next.governAiControls',
  //   path: '/govern/ai-controls',
  //   icon: 'Sparkles',
  // },
  // {
  //   id: 'govern-execution-posture',
  //   label: 'nav.next.governExecutionPosture',
  //   path: '/govern/execution-posture',
  //   icon: 'Gauge',
  // },
  // {
  //   id: 'govern-audit-posture',
  //   label: 'nav.next.governAuditPosture',
  //   path: '/govern/audit-posture',
  //   icon: 'ShieldCheck',
  // },
  // {
  //   id: 'govern-hybrid-byo',
  //   label: 'nav.next.governHybridByo',
  //   path: '/govern/hybrid-worker-byo',
  //   icon: 'Server',
  // },
];

/**
 * Next shell sidebar — grouped IA (DC-324 / mockup). Shown only when `eligibleForNextShell`.
 * Develop phase links use filtered lists (DC-330).
 */
export const NEXT_SHELL_NAV_ITEMS: NavigationItem[] = [
  {
    id: 'home',
    label: 'nav.next.home',
    path: '/',
    icon: 'LayoutDashboard',
  },
  {
    id: 'inbox',
    label: 'nav.inbox',
    path: '/inbox',
    icon: 'Inbox',
  },
  {
    id: 'develop',
    label: 'nav.next.develop',
    path: '/develop/work-items',
    icon: 'Layers',
    children: [
      {
        id: 'develop-work-items',
        label: 'nav.workItems',
        path: '/develop/work-items',
        icon: 'CheckSquare',
        children: [
          {
            id: 'develop-define',
            label: 'nav.next.developDefine',
            path: '/develop/work-items/define',
            icon: 'ListTodo',
          },
          {
            id: 'develop-implement',
            label: 'nav.next.developImplement',
            path: '/develop/work-items/build',
            icon: 'SquareCode',
          },
          {
            id: 'develop-verify',
            label: 'nav.next.developVerify',
            path: '/develop/work-items/verify',
            icon: 'GitPullRequest',
          },
        ],
      },
      {
        id: 'develop-pull-requests',
        label: 'nav.pullRequests',
        path: '/develop/pull-requests',
        icon: 'GitPullRequest',
      },
    ],
  },
  {
    id: 'inspect',
    label: 'nav.next.inspect',
    path: '/inspect/quality',
    icon: 'ScanSearch',
    children: [
      {
        id: 'inspect-quality',
        label: 'nav.next.inspectQuality',
        path: '/inspect/quality',
        icon: 'ClipboardCheck',
      },
      {
        id: 'inspect-leaderboard',
        label: 'nav.leaderboard',
        path: '/leaderboard',
        icon: 'Trophy',
      },
      {
        id: 'inspect-engineers',
        label: 'nav.engineers',
        path: '/engineers',
        icon: 'Users',
      },
      {
        id: 'inspect-teams',
        label: 'nav.teams',
        path: '/teams',
        icon: 'Network',
      },
      {
        id: 'inspect-repositories',
        label: 'nav.repositories',
        path: '/repositories',
        icon: 'FolderGit2',
      },
    ],
  },
  {
    id: 'govern',
    label: 'nav.next.govern',
    path: '/govern/adrs',
    icon: 'Scale',
    collapsible: true,
  },
  {
    id: 'settings',
    label: 'nav.settings',
    path: '/settings',
    icon: 'Settings',
  },
];

export function getGovernHubNavItems(options?: {
  governanceStandardsWorkflowsEnabled?: boolean;
}): NavigationItem[] {
  if (options?.governanceStandardsWorkflowsEnabled === false) {
    return GOVERN_HUB_NAV_ITEMS.filter((item) => item.id !== 'govern-coding-standards');
  }
  return GOVERN_HUB_NAV_ITEMS;
}

/** Depth-first list of sidebar rows for palette catalog (parents + nested leaves). */
export function flattenNavigationItems(items: NavigationItem[]): NavigationItem[] {
  const out: NavigationItem[] = [];
  for (const item of items) {
    out.push(item);
    if (item.children?.length) {
      out.push(...flattenNavigationItems(item.children));
    }
  }
  return out;
}

export function getSidebarNavItems(
  eligibleForNextShell: boolean,
  options?: { governanceStandardsWorkflowsEnabled?: boolean }
): NavigationItem[] {
  if (!eligibleForNextShell) {
    return NAV_ITEMS;
  }

  const governChildren = getGovernHubNavItems(options);

  return NEXT_SHELL_NAV_ITEMS.map((item) =>
    item.id === 'govern' ? { ...item, children: governChildren } : item
  );
}

/** Sidebar destinations flattened for search palette catalog. */
export function getSearchNavigationItems(
  eligibleForNextShell: boolean,
  options?: { governanceStandardsWorkflowsEnabled?: boolean }
): NavigationItem[] {
  return flattenNavigationItems(getSidebarNavItems(eligibleForNextShell, options));
}
